import DiscussionList from '@/js/shared/components/discussion/DiscussionList';
import { useEventEmitter } from '@byteclaw/use-event-emitter';
import {
  Box,
  Button,
  Icon,
  IconButton,
  InputAdornment,
  InputBase,
  Tab,
  Tabs,
  Typography,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import axios from 'axios';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { withRouter } from 'react-router-dom';
import ChatBubble from '../../../shared/components/ui/ChatBubble';
import ResponsiveButton from '../../../shared/components/ui/ResponsiveButton';
import UserAvatar from '../../../shared/components/ui/UserAvatar';
import { AuthContext } from '../../../shared/context/AuthContext';
import { ConferenceContext } from '../../../shared/context/ConferenceContext';
import { PusherContext } from '../../../shared/context/PusherContext';
import { infoList } from '../../../shared/utils/DataFormat';
import { EntityBookingList } from '../bookings/EntityBookingList';

const useParticipantStyles = makeStyles((theme) => ({
  avatarHolder: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.background.shadedown_10,
    },
  },
  avatarHost: {
    position: 'relative',
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      content: '""',
      borderWidth: 2,
      borderStyle: 'solid',
      borderColor: theme.palette.primary.main,
      borderRadius: 22,
      width: 22,
      height: 22,
    },
  },
  userName: {
    textDecoration: 'underline',
    textTransform: 'capitalize',
    padding: 0,
    marginRight: 5,
    lineHeight: '1rem',
    // color: '#FFFFFF',
    opacity: 0.8,
    '&:hover': {
      opacity: 1,
      textDecoration: 'underline',
    },
  },
  profileInfo: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  endIcon: {
    marginLeft: 0,
  },
}));

const ListParticipant = React.memo(
  (props) => {
    const classes = useParticipantStyles();
    const { openUserPopup } = useContext(ConferenceContext);

    return (
      <Box
        flexGrow={1}
        display="flex"
        alignItems="flex-start"
        justifyContent={props.small ? 'center' : 'normal'}
      >
        <Box
          className={classes.avatarHolder}
          pr={props.small ? 0 : 1}
          onClick={() => openUserPopup(props.user)}
        >
          {props.user.isHost ? (
            <Box className={classes.avatarHost}>
              <UserAvatar user={props.user} size="small" />
            </Box>
          ) : (
            <UserAvatar user={props.user} size="small" />
          )}
        </Box>
        {!props.small && (
          <Box flexGrow={1}>
            <Button
              className={classes.userName}
              classes={{ endIcon: classes.endIcon }}
              component="a"
              onClick={() => openUserPopup(props.user)}
              endIcon={<Icon>keyboard_arrow_down</Icon>}
            >
              {props.user.full_name}
            </Button>
            <Typography variant="body2" className={classes.profileInfo}>
              <em>
                {infoList([
                  props.user.title,
                  props.user.company,
                  props.user.location_city,
                ])}
              </em>
            </Typography>
          </Box>
        )}
      </Box>
    );
  },
  (nextProps, prevProps) => nextProps.small === prevProps.small
);

const videoChatStyles = makeStyles((theme) => ({
  holder: {
    // margin: '0 -8px',
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
  },
  contentScroll: {
    overflowX: 'hidden',
    overflowY: 'auto',
  },
  // commentHolder: {
  //   position: 'absolute',
  //   top: 0,
  //   left: 0,
  //   right: 0
  // }
}));

const VideoChat = (props) => {
  const classes = videoChatStyles();
  const { authState } = useContext(AuthContext);
  // const inputRef = useRef();
  const [messageText, setMessageText] = useState('');
  const eventEmitter = useEventEmitter();
  const userPopup = (user) => {
    eventEmitter.emit('profile', user);
  };

  const postHandler = (messageText) => {
    axios.post(`/api/visitor/entities/${props.entityId}/sendlivechatmessage`, {
      message: messageText,
    });
    setMessageText('');
  };

  const onFormSubmit = (e) => {
    e.preventDefault();
    postHandler(messageText);
    setMessageText('');
    // send to server with e.g. `window.fetch`
  };
  const watchKeypress = (e) => {
    if (e.which === 13 && !e.shiftKey) {
      e.preventDefault();
      if (messageText.length > 0) {
        postHandler(messageText);
      }
      setMessageText('');
    }
  };
  // const addEmoji = (emoji) => {
  //   setMessageText((prev) => `${prev}${String.fromCodePoint(emoji)}`);
  //   setPopperOpen(false);
  //
  //   inputRef.current.focus();
  // };
  const handleInputChange = (e) => {
    setMessageText(e.target.value);
  };

  return (
    <Box
      className={classes.holder}
      flexGrow={1}
      display="flex"
      flexDirection="column"
    >
      <Box
        className={classes.commentHolder}
        display="flex"
        alignItems="center"
        pl={1}
      >
        <Box p={0.5}>
          <UserAvatar
            className={classes.commentAvatar}
            user={authState.user}
            size="small"
          />
        </Box>
        <Box flexGrow={1} p={0.5}>
          {props.disabled ? (
            <Box p={1}>Posting closed</Box>
          ) : (
            <form onSubmit={onFormSubmit}>
              <InputBase
                value={messageText}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      className={classes.emojiOpen}
                      color="primary"
                      aria-label="Send"
                      onClick={onFormSubmit}
                    >
                      <Icon>send</Icon>
                    </IconButton>
                  </InputAdornment>
                }
                multiline={true}
                rowsMax={3}
                onKeyPress={watchKeypress}
                onChange={handleInputChange}
                fullWidth
                placeholder="Message..."
                id="comment"
                name="comment"
                inputProps={{ 'aria-label': 'Message...' }}
                className={classes.commentField}
              />
            </form>
          )}
        </Box>
      </Box>

      <Box className={classes.contentScroll} flexGrow={1}>
        {props.messages.length === 0 && (
          <Box p={1} pt={3} align="center">
            Messages are not saved and viewed only by those currently in the
            room
          </Box>
        )}

        {props.messages.map((message, index) => {
          return message.tempText ? (
            <ChatBubble key={index} tempText={message.tempText} />
          ) : (
            <ChatBubble
              userPopup={userPopup}
              socketMessage={true}
              key={message.id}
              user={authState.user}
              message={message}
            />
          );
        })}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  holder: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
  },
  chatHolder: {
    //width: '100%',
    //height: 300,
    position: 'relative',
  },
  smallButton: {
    padding: '2px 4px',
    marginBottom: 2,
    marginRight: 1,
  },
  embedHolder: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.palette.background.shadedown_20,
  },
  videoEmbed: {
    position: 'absolute',
    overflow: 'hidden',
    top: 0,
    left: 0,
    right: -1,
    bottom: -1,
  },
  discussionPopup: {
    opacity: 0.85,
    textAlign: 'left',
    backgroundColor: theme.palette.background.default,
    borderRadius: 5,
    filter: `drop-shadow(5px 5px 5px rgba(0, 0, 0, 0.3))`,
    position: 'absolute',
    padding: 8,
    bottom: 65,
    left: 50,
    right: 5,
    height: '50vh',
    overflowX: 'hidden',
    overflowY: 'auto',
  },
  videoEmbedLeave: {
    // backgroundColor: theme.palette.background.default,
    borderRadius: 5,
    position: 'absolute',
    bottom: 16,
    right: 10,
  },
  videoEmbedJoin: {
    // backgroundColor: theme.palette.background.default,
    padding: 10,
    borderRadius: 5,
    position: 'absolute',
    width: '100%',
    bottom: 0,
    //left: '50%',
    //marginLeft: -125
  },
  memberList: {
    overflowX: 'hidden',
    overflowY: 'auto',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  participantsLarge: {
    width: 260,
    overflow: 'hidden',
  },
  participantsSmall: {
    width: 50,
    overflow: 'hidden',
  },
  navContent: {
    position: 'relative',
    margin: '0 -8px',
  },
  waitingHeader: {
    opacity: 0.4,
  },
  chatIcon: {
    fontSize: 18,
    verticalAlign: 'middle',
  },
  tabHolder: {
    minHeight: 28,
  },
  tabItem: {
    minWidth: 'auto',
    lineHeight: '1.2rem',
    minHeight: 25,
    padding: '0 4px',
    flexGrow: 1,
  },
  tabExtra: {
    opacity: 0.8,
  },
  menuToggle: {
    // backgroundColor: '#191919',
    // marginTop: -8,
    // marginLeft: -8,
    // marginRight: -8,
    // marginBottom: 8
  },
}));

const EntityVideoHandler = withRouter((props) => {
  const { channels, ROOM_TYPE, actions: pusherActions } = useContext(
    PusherContext
  ); // Hämta Context

  const classes = useStyles();
  const showChatRef = useRef();
  const iframeRefStore = useRef();
  const [members, setMembers] = useState([]);
  const [queue, setQueue] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [showDiscussion, toggleDiscussion] = useState(false);
  const [showBookings, toggleShowBookings] = useState(false);
  const [selectedNav, selectNav] = useState('people');
  const [showParticipantMenu, toggleParticipantMenu] = useState(true);
  const [userSetting, setUserSetting] = useState({
    isPublisher: props.isHost,
    isMember: props.isHost || !props.useQueue, // Join directly if host or not Wa
    inQueue: !props.isHost && props.useQueue,
  });
  const queueList = queue.filter((qv) => !props.hosts.includes(qv.id));
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'), {
    noSsr: true,
  });

  const iframeRef = useCallback((node) => {
    if (iframeRefStore.current) {
      iframeRefStore.current.removeEventListener(
        'showprofile',
        showProfileEvent
      );
    }
    if (node !== null) {
      node.addEventListener('showprofile', showProfileEvent);
      iframeRefStore.current = node;
    }
  }, []);

  const { authState } = useContext(AuthContext);

  const eventEmitter = useEventEmitter();
  const meetingEvents = props.entity.entityList.events.filter(
    (event) => event.booking_settings
  );
  const showProfileEvent = (e) => {
    eventEmitter.emit('profile', e.detail);
  };
  showChatRef.current = true;

  const admitFromQueue = (visitorId) => {
    axios.put(`/api/visitor/entities/${props.entityId}/sendlivemessage`, {
      visitorId: visitorId,
      event: 'is-member',
      message: true,
    });
  };
  const kickFromMembers = (visitorId) => {
    axios.put(`/api/visitor/entities/${props.entityId}/sendlivemessage`, {
      visitorId: visitorId,
      event: 'is-member',
      message: false,
    });
  };

  const togglePublisher = (isPublisher) => {
    setUserSetting({ ...userSetting, isPublisher: isPublisher });
  };

  const changeNav = (e, newNav) => {
    selectNav(newNav);
  };

  const addChatMessage = (data) => {
    setChatMessages((currentMessages) => [data, ...currentMessages]);
  };

  // const openSnack = (msg) => {
  //   addChatMessage({ content: { reason : 'sdfasd'}})
  // }

  useEffect(() => {
    if (userSetting.inQueue || props.isHost) {
      const queueChannel = pusherActions.subscribe(
        ROOM_TYPE.ENTITY_QUEUE,
        `${props.entityId}`,
        'presence'
      );
      const membersUpdate = () => {
        setQueue(Object.values(queueChannel.members.members));
      };

      queueChannel.bind('pusher:subscription_succeeded', membersUpdate);
      queueChannel.bind('pusher:member_added', membersUpdate);
      queueChannel.bind('pusher:member_removed', membersUpdate);
      queueChannel.bind('pusher:subscription_error', (error) => {
        console.log('presence ERROR', error);
      });
    }
    return () => {
      if (userSetting.inQueue) {
        pusherActions.unSubscribe(
          ROOM_TYPE.ENTITY_QUEUE,
          `${props.entityId}`,
          'presence'
        );
      }
    };
  }, [userSetting.inQueue]);

  useEffect(() => {
    if (userSetting.isMember) {
      // When open live or
      const memberChannel = pusherActions.subscribe(
        ROOM_TYPE.ENTITY_CHAT,
        `${props.entityId}`,
        'presence'
      );
      const membersUpdate = () => {
        setMembers(
          Object.values(memberChannel.members.members)
            .map((u) => ({ ...u, isHost: props.hosts.includes(u.id) }))
            .sort((a, b) => {
              return a.isHost < b.isHost ? 1 : -1;
            })
        );
      };

      memberChannel.bind('pusher:subscription_succeeded', membersUpdate);
      memberChannel.bind('pusher:member_added', membersUpdate);
      memberChannel.bind('pusher:member_removed', membersUpdate);
      memberChannel.bind('pusher:subscription_error', (error) => {
        console.log('presence ERROR', error);
      });
      memberChannel.bind('chat-message', (data) => {
        addChatMessage(data);
      });
    }
    return () => {
      console.log('unsubscribe isMember', userSetting.isMember);
      if (userSetting.isMember) {
        pusherActions.unSubscribe(
          ROOM_TYPE.ENTITY_CHAT,
          `${props.entityId}`,
          'presence'
        );
      }
    };
  }, [userSetting.isMember]);

  useEffect(() => {
    // Connect to a private channel, to receive changes in isMember
    const privateChannel = pusherActions.subscribe(
      ROOM_TYPE.ENTITY_CHAT_PRIVATE,
      `${props.entityId}_${authState.user.id}`
    );
    privateChannel.bind('is-member', (isMember) => {
      console.log('is-member', isMember);
      setUserSetting({
        ...userSetting,
        isMember: isMember,
        inQueue: !isMember,
      });
    });
    return () => {
      pusherActions.unSubscribe(
        ROOM_TYPE.ENTITY_CHAT_PRIVATE,
        `${props.entityId}_${authState.user.id}`
      );
    };
  }, []);

  return userSetting.inQueue ? (
    <Box
      className={classes.holder}
      display="flex"
      flexGrow={1}
      justifyContent="center"
      alignItems="center"
    >
      <Box align="center">Waiting for host to let you in</Box>
    </Box>
  ) : (
    <Box display="flex" className={classes.holder}>
      <Box flexGrow={1} className={classes.chatHolder}>
        <Box className={classes.embedHolder}>
          <iframe
            data-action="hej"
            id="videoiframe"
            ref={iframeRef}
            className={classes.videoEmbed}
            src={`/webrtcembed2?roomId=${props.entityId}&justwatch=${
              userSetting.isPublisher ? '0' : '1'
            }&gallery=1&host=${props.isHost ? 'true' : 'false'}&breakout=${
              props.isBreakout ? 'true' : 'false'
            }`}
            frameBorder="0"
            width="100%"
            height="100%"
            scrolling="no"
            allow="autoplay; microphone; camera"
          ></iframe>

          {showDiscussion && (
            <Box className={classes.discussionPopup}>
              <DiscussionList
                listType="connection"
                location={props.location}
                history={props.history}
                isEmbedded={true}
                connectionType="entity"
                connectionId={props.entityId}
                hideMenu={true}
                title={props.entity.extra_data.discussion.headline || 'Forum'}
              />
            </Box>
          )}
          {userSetting.isPublisher ? (
            <Box align="center" className={classes.videoEmbedLeave}>
              {props.entity.extra_data.discussion &&
                props.entity.extra_data.discussion.togglebutton !== ' ' && (
                  <ResponsiveButton
                    variant="contained"
                    small={smallScreen}
                    color={showDiscussion ? 'secondary' : 'default'}
                    icon={showDiscussion ? 'expand_more' : 'expand_less'}
                    onClick={() => toggleDiscussion(!showDiscussion)}
                  >
                    {props.entity.extra_data.discussion.togglebutton || 'Forum'}
                  </ResponsiveButton>
                )}{' '}
              <ResponsiveButton
                variant="contained"
                small={smallScreen}
                icon="videocam_off"
                onClick={() => togglePublisher(false)}
              >
                Leave conversation
              </ResponsiveButton>
            </Box>
          ) : (
            <Box align="center" className={classes.videoEmbedJoin}>
              {props.entity.extra_data.discussion && (
                <ResponsiveButton
                  variant="contained"
                  small={smallScreen}
                  color={showDiscussion ? 'secondary' : 'default'}
                  icon={showDiscussion ? 'expand_more' : 'expand_less'}
                  onClick={() => toggleDiscussion(!showDiscussion)}
                >
                  {props.entity.extra_data.discussion.togglebutton || 'Forum'}
                </ResponsiveButton>
              )}{' '}
              <ResponsiveButton
                variant="contained"
                icon="videocam"
                onClick={() => togglePublisher(true)}
              >
                Join conversation
              </ResponsiveButton>
              <Box>Interact with voice & video</Box>
            </Box>
          )}
        </Box>
      </Box>
      <Box
        align="left"
        pl={1}
        pr={1}
        className={
          showParticipantMenu
            ? classes.participantsLarge
            : classes.participantsSmall
        }
        display="flex"
        flexDirection="column"
      >
        <Box>
          {smallScreen && (
            <Box>
              {showParticipantMenu ? (
                <Box pb={1} className={classes.menuToggle}>
                  <Button
                    size="small"
                    onClick={() => toggleParticipantMenu(!showParticipantMenu)}
                    startIcon={<Icon>close</Icon>}
                  >
                    Hide menu
                  </Button>
                </Box>
              ) : (
                <Box className={classes.menuToggle}>
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => toggleParticipantMenu(!showParticipantMenu)}
                  >
                    <Icon>menu_open</Icon>
                  </IconButton>
                </Box>
              )}
            </Box>
          )}

          {showParticipantMenu ? (
            <Box pb={2}>
              <Tabs
                className={classes.tabHolder}
                value={selectedNav}
                onChange={changeNav}
                indicatorColor="primary"
                textColor="primary"
                centered
              >
                <Tab
                  value={'people'}
                  className={classes.tabItem}
                  label={
                    <span>
                      <Icon className={classes.chatIcon}>people</Icon> People{' '}
                      <span className={classes.tabExtra}>
                        {members.length}
                        {props.useQueue &&
                          props.isHost &&
                          `/${queueList.length}`}
                      </span>
                    </span>
                  }
                />
                <Tab
                  value={'chat'}
                  className={classes.tabItem}
                  label={
                    <span>
                      <Icon className={classes.chatIcon}>chat</Icon> Chat{' '}
                      <span className={classes.tabExtra}>
                        {chatMessages.length}
                      </span>
                    </span>
                  }
                />
              </Tabs>
            </Box>
          ) : (
            <Box align="center">
              <Icon>people</Icon>
              <br />
              {members.length}
              {props.useQueue && props.isHost && `/${queueList.length}`}
              <br />
              <br />
              <Icon>chat</Icon>
              <br />
              {chatMessages.length}
            </Box>
          )}
        </Box>
        {showParticipantMenu && (
          <Box flexGrow={1} className={classes.navContent} display="flex">
            {selectedNav === 'chat' ? (
              <VideoChat messages={chatMessages} entityId={props.entityId} />
            ) : (
              <Box className={classes.memberList} flexGrow={1}>
                {props.isHost && meetingEvents.length > 0 && (
                  <Box pb={1} textAlign="center">
                    <Button
                      size="small"
                      startIcon={<Icon>bookmark</Icon>}
                      color={showBookings ? 'default' : 'secondary'}
                      onClick={() => toggleShowBookings(!showBookings)}
                    >
                      {showBookings ? 'Hide' : 'Show'} bookings
                    </Button>

                    {showBookings &&
                      meetingEvents.map((event) => (
                        <Box p={1} key={event.id}>
                          <EntityBookingList
                            entityId={props.entity.id}
                            event={event}
                            hasAdminAccess={props.entity.hasAdminAccess}
                            bookingUpdates={props.bookingUpdates}
                            gridSize={12}
                          />
                        </Box>
                      ))}
                  </Box>
                )}

                {userSetting.isMember && (
                  <>
                    <Box pt={0} p={1}>
                      {members.map((m) => (
                        <Box
                          key={m.id}
                          display="flex"
                          alignItems="center"
                          pb={1}
                        >
                          <ListParticipant
                            user={m}
                            key={m.id}
                            small={!showParticipantMenu}
                          />
                          {props.useQueue &&
                            props.isHost &&
                            showParticipantMenu && (
                              <Box>
                                {authState.user.id !== m.id && !m.isHost && (
                                  <Button
                                    color="primary"
                                    size="small"
                                    onClick={() => kickFromMembers(m.id)}
                                  >
                                    Kick out
                                  </Button>
                                )}
                              </Box>
                            )}
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
                {props.useQueue && props.isHost && (
                  <>
                    {showParticipantMenu ? (
                      <Box align="center" className={classes.waitingHeader}>
                        Waiting list ({queueList.length})
                      </Box>
                    ) : (
                      <Box align="center" className={classes.waitingHeader}>
                        <Icon>block</Icon>
                      </Box>
                    )}

                    <Box pt={1} pb={1}>
                      {queueList.map((m) => (
                        <Box key={m.id} display="flex" alignItems="center">
                          <ListParticipant
                            user={m}
                            key={m.id}
                            small={!showParticipantMenu}
                          />
                          {props.useQueue && (
                            <Box>
                              {authState.user.id !== m.id && (
                                <Button
                                  size="small"
                                  color="primary"
                                  onClick={() => admitFromQueue(m.id)}
                                >
                                  Admit
                                </Button>
                              )}
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
});

export default EntityVideoHandler;
